import { cn } from '@/utils/cn';
import type { TextareaHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className, id, required, ...props }, ref) => {
    const inputId = id ?? props.name;
    const { required: isRequired } = parseFieldLabel(label, required);
    return (
      <div className="w-full">
        <FieldLabel htmlFor={inputId} label={label} required={required} />
        {hint && !error && (
          <p className="mb-1.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p>
        )}
        <textarea
          ref={ref}
          id={inputId}
          required={isRequired}
          className={cn('input min-h-[5rem] resize-y', error && 'input-error', className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          {...props}
        />
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-xs text-error-700 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';
